/**
 * Tiny URL <-> app-state mapping. No router dependency.
 *
 * Shapes:
 *   /                                           → docs view (or restore last)
 *   /docs                                       → docs, no repo
 *   /docs/{owner}/{name}                        → docs, repo selected
 *   /docs/{owner}/{name}/{path...}              → docs, repo + path
 *   /admin                                      → admin, default tab
 *   /admin/{tab}                                → admin, specific tab
 *
 * MSAL auth redirects use the same path; the URL stays stable across the
 * round-trip because External ID preserves it.
 */
export type AdminTab = 'org' | 'repos' | 'roles' | 'users' | 'ai' | 'audit';
const ADMIN_TABS: ReadonlySet<string> = new Set(['org', 'repos', 'roles', 'users', 'ai', 'audit']);

export interface AppRoute {
    view: 'docs' | 'admin';
    repoId: string | null;     // 'owner/name' lowercased
    path: string | null;
    adminTab: AdminTab;
}

export function parseRoute(pathname: string): AppRoute {
    const segs = pathname.split('/').filter(Boolean);
    if (segs.length === 0 || segs[0] === 'docs') {
        const rest = segs[0] === 'docs' ? segs.slice(1) : segs;
        if (rest.length >= 2) {
            const owner = decodeURIComponent(rest[0]);
            const name = decodeURIComponent(rest[1]);
            const path = rest.length > 2 ? rest.slice(2).map(decodeURIComponent).join('/') : null;
            return {
                view: 'docs',
                repoId: `${owner.toLowerCase()}/${name.toLowerCase()}`,
                path,
                adminTab: 'org'
            };
        }
        return { view: 'docs', repoId: null, path: null, adminTab: 'org' };
    }
    if (segs[0] === 'admin') {
        const tab = segs[1] && ADMIN_TABS.has(segs[1]) ? (segs[1] as AdminTab) : 'org';
        return { view: 'admin', repoId: null, path: null, adminTab: tab };
    }
    // Unknown prefix → fall back to docs root
    return { view: 'docs', repoId: null, path: null, adminTab: 'org' };
}

export function buildRoute(r: AppRoute): string {
    if (r.view === 'admin') {
        return `/admin/${r.adminTab}`;
    }
    if (!r.repoId) return '/docs';
    const [owner, name] = r.repoId.split('/');
    const base = `/docs/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`;
    if (!r.path) return base;
    return `${base}/${r.path.split('/').map(encodeURIComponent).join('/')}`;
}

export function isAdminTab(s: string | undefined | null): s is AdminTab {
    return typeof s === 'string' && ADMIN_TABS.has(s);
}
